import type { Board } from "../../engine/board";
import type { Sound } from "../../engine/things/sounds/sound";
import type { XY } from "../../engine/xy";
import { SoundLoader } from "./soundLoader";

const MAX_SOUNDS_PLAYING_AT_SAME_TIME = 24;
const VOLUME_DECREASE_PER_UNIT_OF_DISTANCE = 0.019;
const BALANCE_SHIFT_PER_UNIT_OF_DISTANCE = 0.05;

interface ManagedSoundEntry {
	sound: Sound;
	sourceNode: AudioBufferSourceNode | null;
	gainNode: GainNode | null;
	pannerNode: StereoPannerNode | null;
	playing: boolean;
	finishedPlaying: boolean;
	// Per-file gain multiplier, resolved once the random file variant is picked.
	fileVolumeBoost: number;
}

export class SoundManager {
	private audioContext: AudioContext;
	private soundLoader: SoundLoader;
	private compressor: DynamicsCompressorNode;
	private listenerXY: XY | null = null;
	private listenerBoard: Board | null = null;
	private managedSounds = new Map<Sound, ManagedSoundEntry>();
	private volumeLevel: number = 1.0;

	constructor(audioContext: AudioContext, soundLoader: SoundLoader) {
		this.audioContext = audioContext;
		this.soundLoader = soundLoader;
		this.compressor = audioContext.createDynamicsCompressor();
		this.compressor.connect(audioContext.destination);
	}

	setListenerXY(xy: XY, board: Board): void {
		this.listenerXY = xy;
		this.listenerBoard = board;
	}

	manageSound(sound: Sound): void {
		if (this.managedSounds.has(sound)) return;
		this.managedSounds.set(sound, {
			sound,
			sourceNode: null,
			gainNode: null,
			pannerNode: null,
			playing: false,
			finishedPlaying: false,
			fileVolumeBoost: 1.0,
		});
	}

	async playSounds(): Promise<void> {
		const entries = Array.from(this.managedSounds.values());

		entries.sort((a, b) => {
			const s1 = a.sound;
			const s2 = b.sound;

			// No-source sounds first (hasSource false < true)
			if (s1.hasSource() !== s2.hasSource()) {
				return s1.hasSource() ? 1 : -1;
			}

			// Looping sounds first
			if (s1.loop !== s2.loop) {
				return s2.loop ? 1 : -1;
			}

			// Newer sounds first (higher turnAddedToBoard)
			if (s1.turnAddedToBoard !== s2.turnAddedToBoard) {
				return s2.turnAddedToBoard - s1.turnAddedToBoard;
			}

			// Closer sounds first (if both have sources)
			if (s1.hasSource() && s2.hasSource() && this.listenerXY) {
				const d1 = s1.xy!.distanceTo(this.listenerXY);
				const d2 = s2.xy!.distanceTo(this.listenerXY);
				if (d1 !== d2) return d1 - d2;
			}

			// Higher volume first
			if (s1.volume !== s2.volume) {
				return s2.volume - s1.volume;
			}

			return 0;
		});

		let soundsPlaying = 0;
		const toRemove: Sound[] = [];

		for (const entry of entries) {
			const soundFinishedPlaying = entry.finishedPlaying;
			const inactiveLoopingSound = entry.sound.loop && !entry.sound.active;
			const soundIsFromAnotherBoard =
				this.listenerBoard !== null && entry.sound.board !== null && entry.sound.board !== this.listenerBoard;

			const shouldPlay =
				soundsPlaying < MAX_SOUNDS_PLAYING_AT_SAME_TIME &&
				!soundFinishedPlaying &&
				!soundIsFromAnotherBoard &&
				!inactiveLoopingSound;

			if (shouldPlay) {
				soundsPlaying++;
				await this.playSound(entry);
			} else {
				this.stopEntry(entry);
				toRemove.push(entry.sound);
			}
		}

		for (const sound of toRemove) {
			this.managedSounds.delete(sound);
		}
	}

	private async playSound(entry: ManagedSoundEntry): Promise<void> {
		if (entry.playing && entry.sourceNode) {
			this.transformSound(entry);
			return;
		}

		const filename = SoundLoader.getFileNameForSoundId(entry.sound.soundId);
		if (!filename) {
			entry.finishedPlaying = true;
			return;
		}
		const buffer = await this.soundLoader.loadAudioBuffer(filename);
		if (!buffer) {
			entry.finishedPlaying = true;
			return;
		}
		entry.fileVolumeBoost = SoundLoader.getVolumeBoostForFile(filename);

		// Re-check after async load: another frame's playSounds() may have already
		// started this entry, or stopped/removed it while the buffer was loading.
		if (entry.playing || !this.managedSounds.has(entry.sound)) return;

		const source = this.audioContext.createBufferSource();
		source.buffer = buffer;

		const variation = entry.sound.randomPitchVariation;
		source.playbackRate.value = 1 + Math.random() * variation * 2 - variation;

		if (entry.sound.loop) {
			source.loop = true;
		} else {
			source.onended = () => {
				entry.finishedPlaying = true;
				entry.playing = false;
			};
		}

		const gainNode = this.audioContext.createGain();
		const pannerNode = this.audioContext.createStereoPanner();

		source.connect(gainNode);
		gainNode.connect(pannerNode);
		pannerNode.connect(this.compressor);

		entry.sourceNode = source;
		entry.gainNode = gainNode;
		entry.pannerNode = pannerNode;
		entry.playing = true;

		this.transformSound(entry);
		source.start();
	}

	private transformSound(entry: ManagedSoundEntry): void {
		if (!entry.gainNode || !entry.pannerNode) return;

		let volume = entry.sound.volume;
		let balance = 0;

		if (entry.sound.hasSource() && this.listenerXY) {
			const source = entry.sound.xy!;
			const distance = source.distanceTo(this.listenerXY);
			volume = volume - distance * VOLUME_DECREASE_PER_UNIT_OF_DISTANCE * entry.sound.attenuationFactor;
			if (volume < entry.sound.minVolume) volume = entry.sound.minVolume;
			if (volume < 0) volume = 0;

			const deltaX = source.x - this.listenerXY.x;
			balance = deltaX * BALANCE_SHIFT_PER_UNIT_OF_DISTANCE;
			if (balance < -1) balance = -1;
			if (balance > 1) balance = 1;
		}

		volume = volume * this.volumeLevel * entry.fileVolumeBoost;
		entry.gainNode.gain.value = volume;
		entry.pannerNode.pan.value = balance;
	}

	private stopEntry(entry: ManagedSoundEntry): void {
		if (entry.sourceNode && entry.playing) {
			try {
				entry.sourceNode.stop();
				entry.sourceNode.disconnect();
			} catch {
				// Ignore errors from already-stopped nodes
			}
		}
		if (entry.gainNode) entry.gainNode.disconnect();
		if (entry.pannerNode) entry.pannerNode.disconnect();
		entry.sourceNode = null;
		entry.gainNode = null;
		entry.pannerNode = null;
		entry.playing = false;
	}

	clear(): void {
		for (const entry of this.managedSounds.values()) {
			this.stopEntry(entry);
		}
		this.managedSounds.clear();
	}

	setVolumeLevel(level: number): void {
		this.volumeLevel = level;
	}
}
